/**
 * The trail, finally readable.
 *
 * `access_audit` has recorded every role change, every grant and every team
 * approval since migration 0002, and nothing in this console has ever read it.
 * `APPROVING-TEAMS.md` tells operators the record exists, says it names who did
 * it and when, and promises it outlives the team row — all true of the database
 * and, until this screen, reachable by nobody the promise was made to.
 *
 * It is also what makes the other screens checkable. Teams shows what a team
 * holds *now*; only this answers "when did we lend that account, and who did
 * it".
 *
 * What each row *says* is decided in lib/audit-copy.ts, under test. This file
 * renders finished answers and holds no rule of its own — the same split the
 * Overview screen took, for the same reason: a `.tsx` is unreachable by the
 * runner, and this file has already produced two logic errors that only a
 * person pulling a function out by hand ever found.
 */
import { useState } from "react";
import { type AuditEntry, api } from "../lib/api.ts";
import {
	type Party,
	detailPanel,
	footerNote,
	parties,
	trailKey,
	whatCell,
	whenLabel,
} from "../lib/audit-copy.ts";
import { readFailure } from "../lib/read-failure.ts";
import { useLoad } from "../lib/useLoad.ts";
import { Badge, Button, InfoBanner, Page, Table } from "../ui/index.ts";

export function Audit() {
	const [entries, reload] = useLoad(() => api.audit());
	// Loaded beside the trail purely to put names on its uuids. Both are
	// admin-only, like this screen; a failure leaves the uuids showing rather
	// than the page.
	const [members] = useLoad(() => api.members());
	const [teams] = useLoad(() => api.teams());
	const [open, setOpen] = useState<string | null>(null);

	const rows = entries.state === "ready" ? entries.data.entries : [];

	const byUser = new Map<string, string>();
	if (members.state === "ready")
		for (const m of members.data.members) if (m.email) byUser.set(m.userId, m.email);
	const byOrg = new Map<string, string>();
	if (teams.state === "ready") for (const t of teams.data.teams) byOrg.set(t.id, t.name);

	const note = footerNote(rows.length);

	return (
		<Page
			title="What was done, and by whom"
			description="Every role change, every asset lent and every team approval, oldest at the bottom. Append-only: the record survives the member and the team it describes, which is the point of keeping it."
			actions={
				<Button variant="outline" size="sm" onClick={reload}>
					Refresh
				</Button>
			}
		>
			{entries.state === "failed" &&
				(() => {
					// By the status: «visible to admins only» is a 403's sentence and no other's.
					const f = readFailure(entries.status, {
						what: "The trail",
						forbidden: "The trail is visible to admins only.",
					});
					return (
						<InfoBanner variant={f.variant} title={f.title}>
							{entries.why}
						</InfoBanner>
					);
				})()}

			<Table<AuditEntry>
				columns={[
					{
						id: "what",
						header: "What",
						accessor: (e) => {
							const what = whatCell(e);
							return (
								<div className="flex flex-col gap-1">
									<Badge variant={what.tone}>{what.label}</Badge>
									{what.detail && <span className="text-xs text-muted">{what.detail}</span>}
								</div>
							);
						},
					},
					{
						id: "who",
						header: "Who did it",
						accessor: (e) => <Named party={parties(e, byUser, byOrg).actor} />,
					},
					{
						id: "subject",
						header: "To whom",
						accessor: (e) => <Named party={parties(e, byUser, byOrg).subject} />,
					},
					{
						id: "when",
						header: "When",
						accessor: (e) => (
							<span className="text-muted" title={e.created_at}>
								{whenLabel(e.created_at, Date.now())}
							</span>
						),
					},
					{
						id: "detail",
						header: "",
						accessor: (e) => {
							const id = trailKey(e);
							// One question, asked once: a row that has nothing to show has no
							// button either, because both come from the same answer.
							if (!detailPanel(e)) return null;
							return (
								<Button variant="ghost" size="sm" onClick={() => setOpen(open === id ? null : id)}>
									{open === id ? "Hide" : "Detail"}
								</Button>
							);
						},
					},
				]}
				data={rows}
				keyExtractor={trailKey}
				failed={entries.state === "failed"}
				loading={entries.state === "loading"}
				emptyMessage="Nothing recorded yet. Approving a team or lending an asset writes the first line."
			/>

			{open && <Detail entry={rows.find((e) => trailKey(e) === open)} />}

			{note && <p className="mt-4 text-xs text-muted">{note}</p>}
		</Page>
	);
}

/**
 * A uuid with a name on it, or the bare uuid when nothing can name it.
 *
 * Which of the two, and what the name is, was decided by `parties()`. This only
 * puts it on the screen.
 */
function Named({ party }: { party: Party }) {
	if (!party.id) return <span className="text-muted">—</span>;
	if (!party.name) return <span className="font-mono text-xs">{party.id}</span>;
	return (
		<span title={party.id}>
			{party.name}
			<span className="ml-1 font-mono text-[10px] text-muted">{party.id.slice(0, 8)}</span>
		</span>
	);
}

function Detail({ entry }: { entry?: AuditEntry }) {
	const panel = entry ? detailPanel(entry) : null;
	if (!panel) return null;
	return (
		<div className="mt-4 border border-line bg-surface p-4">
			<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">
				{panel.heading}
			</p>

			{panel.warning && (
				<p className="mb-3 border-l-2 border-warning pl-3 text-xs text-muted">{panel.warning}</p>
			)}

			<dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
				{panel.fields.map((f) => (
					<div key={f.term} className="contents">
						<dt className="text-muted">{f.term}</dt>
						{/* pre-line so a list of ids renders one per line, not as one run-on. */}
						<dd className="whitespace-pre-line break-all font-mono text-xs">{f.value}</dd>
					</div>
				))}
			</dl>
		</div>
	);
}
